import { useEffect, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";

interface Brick {
  x: number;
  y: number;
  w: number;
  h: number;
  alive: boolean;
  color: string;
}

interface BrickBreakerProps {
  containerRef: React.RefObject<HTMLElement | null>;
  onEnd: () => void;
}

const BRICK_COLORS = [
  "#ef5350", "#ab47bc", "#42a5f5", "#26a69a", "#ffca28",
  "#ff7043", "#66bb6a", "#26c6da", "#7e57c2", "#ec407a",
];

export default function BrickBreaker({ containerRef, onEnd }: BrickBreakerProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef<number>(0);
  const [status, setStatus] = useState<"playing" | "win" | "gameover">("playing");
  const stateRef = useRef({
    paddleX: window.innerWidth / 2 - 60,
    ballX: window.innerWidth / 2,
    ballY: window.innerHeight - 80,
    ballDX: 4,
    ballDY: -5,
    bricks: [] as Brick[],
    score: 0,
    status: "playing" as "playing" | "win" | "gameover",
  });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const W = window.innerWidth;
    const H = window.innerHeight;
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext("2d")!;

    // Build bricks from container elements
    const container = containerRef.current;
    const selectors =
      "h1, h2, h3, h4, h5, h6, p, [class*='MuiCard'], [class*='MuiPaper']";
    const elements = container
      ? Array.from(container.querySelectorAll<HTMLElement>(selectors)).filter(
          (el) => {
            const r = el.getBoundingClientRect();
            return r.width > 40 && r.height > 10 && r.top > 0 && r.top < H * 0.7;
          },
        )
      : [];

    const bricks: Brick[] = elements.slice(0, 30).map((el, i) => {
      const r = el.getBoundingClientRect();
      return {
        x: r.left,
        y: r.top,
        w: Math.min(r.width, 300),
        h: Math.min(r.height, 40),
        alive: true,
        color: BRICK_COLORS[i % BRICK_COLORS.length],
      };
    });

    // Fallback grid if no elements found
    if (bricks.length === 0) {
      const cols = 8;
      const rows = 4;
      const bW = (W - 100) / cols;
      const bH = 30;
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          bricks.push({
            x: 50 + c * bW,
            y: 80 + r * (bH + 8),
            w: bW - 6,
            h: bH,
            alive: true,
            color: BRICK_COLORS[(r * cols + c) % BRICK_COLORS.length],
          });
        }
      }
    }

    stateRef.current.bricks = bricks;
    stateRef.current.ballX = W / 2;
    stateRef.current.ballY = H - 80;
    stateRef.current.paddleX = W / 2 - 60;
    stateRef.current.status = "playing";

    const PADDLE_W = 120;
    const PADDLE_H = 12;
    const BALL_R = 8;

    // Mouse / keyboard paddle control
    const onMouseMove = (e: MouseEvent) => {
      stateRef.current.paddleX = Math.max(
        0,
        Math.min(W - PADDLE_W, e.clientX - PADDLE_W / 2),
      );
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft")
        stateRef.current.paddleX = Math.max(0, stateRef.current.paddleX - 25);
      if (e.key === "ArrowRight")
        stateRef.current.paddleX = Math.min(
          W - PADDLE_W,
          stateRef.current.paddleX + 25,
        );
    };
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("keydown", onKeyDown);

    const draw = () => {
      const s = stateRef.current;
      if (s.status !== "playing") return;

      ctx.clearRect(0, 0, W, H);

      // Draw bricks
      s.bricks.forEach((b) => {
        if (!b.alive) return;
        ctx.fillStyle = b.color;
        ctx.beginPath();
        ctx.roundRect(b.x, b.y, b.w, b.h, 4);
        ctx.fill();
        ctx.strokeStyle = "rgba(255,255,255,0.4)";
        ctx.lineWidth = 1;
        ctx.stroke();
      });

      // Move ball
      s.ballX += s.ballDX;
      s.ballY += s.ballDY;

      // Wall collisions
      if (s.ballX - BALL_R < 0 || s.ballX + BALL_R > W) s.ballDX *= -1;
      if (s.ballY - BALL_R < 0) s.ballDY *= -1;

      // Paddle collision
      const paddleTop = H - 50;
      if (
        s.ballY + BALL_R >= paddleTop &&
        s.ballY + BALL_R <= paddleTop + PADDLE_H &&
        s.ballX >= s.paddleX &&
        s.ballX <= s.paddleX + PADDLE_W
      ) {
        s.ballDY = -Math.abs(s.ballDY);
        // add slight angle based on hit position
        const hitRatio = (s.ballX - s.paddleX) / PADDLE_W - 0.5;
        s.ballDX = hitRatio * 8;
      }

      // Brick collision
      s.bricks.forEach((b) => {
        if (!b.alive) return;
        if (
          s.ballX + BALL_R > b.x &&
          s.ballX - BALL_R < b.x + b.w &&
          s.ballY + BALL_R > b.y &&
          s.ballY - BALL_R < b.y + b.h
        ) {
          b.alive = false;
          s.score++;
          s.ballDY *= -1;
        }
      });

      // Ball lost
      if (s.ballY + BALL_R > H) {
        s.status = "gameover";
        setStatus("gameover");
        return;
      }

      // Win
      if (s.bricks.every((b) => !b.alive)) {
        s.status = "win";
        setStatus("win");
        return;
      }

      // Draw paddle
      ctx.fillStyle = "rgba(255,255,255,0.9)";
      ctx.beginPath();
      ctx.roundRect(s.paddleX, H - 50, PADDLE_W, PADDLE_H, 6);
      ctx.fill();

      // Draw ball
      ctx.fillStyle = "#ffffff";
      ctx.beginPath();
      ctx.arc(s.ballX, s.ballY, BALL_R, 0, Math.PI * 2);
      ctx.fill();

      // Draw score
      ctx.fillStyle = "rgba(255,255,255,0.8)";
      ctx.font = "bold 20px sans-serif";
      ctx.fillText(`⚡ ${s.score}`, 20, 40);

      rafRef.current = requestAnimationFrame(draw);
    };

    rafRef.current = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  return (
    <>
      <canvas
        ref={canvasRef}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          zIndex: 2000,
          background: "rgba(0,0,0,0.85)",
          cursor: "none",
        }}
      />
      {status !== "playing" && (
        <Box
          sx={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%,-50%)",
            zIndex: 2100,
            textAlign: "center",
            color: "#fff",
          }}
        >
          <Typography variant="h2" fontWeight={900} gutterBottom>
            {status === "win" ? "🎉 You Win!" : "💀 Game Over"}
          </Typography>
          <Typography variant="h5" sx={{ opacity: 0.7 }}>
            Score: {stateRef.current.score}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.5, mt: 2 }}>
            Double-press ESC to exit
          </Typography>
        </Box>
      )}
      {status === "playing" && (
        <Box
          sx={{
            position: "fixed",
            bottom: 24,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 2100,
            color: "rgba(255,255,255,0.4)",
            fontSize: "0.75rem",
            pointerEvents: "none",
          }}
        >
          Move mouse or ← → arrow keys · Double-ESC to quit
        </Box>
      )}
    </>
  );
}
